import { useState, type KeyboardEvent } from "react";
import { FileDown, Loader2, Pencil } from "lucide-react";
import { Button, Input, Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@lumanity/ui/primitives";
import { cn } from "@/lib/utils";
import type { Output } from "@/features/outputs/types";
import { childrenOf, measure } from "./outlineModel";
import { useSectionGeneration } from "./generationStreamStore";
import SectionCitations from "./SectionCitations";
import { SectionContent, SectionEditor } from "./SectionEditor";
import type { Section } from "./types";

function draftState(sections: Section[]): string {
  if (sections.some((s) => s.content_origin === "ai_generated")) return "AI drafted";
  if (sections.some((s) => s.content_text.trim())) return "Draft in progress";
  return "Not drafted yet";
}

const OutputTitle = ({ output, canWrite, saving, onRename }: { output: Output; canWrite: boolean; saving: boolean; onRename: (title: string) => void }) => {
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(output.title);
  const limit = output.guidelines.title_limit;
  const shown = editing ? value : output.title;
  const used = limit ? measure(shown, limit) : 0;

  const commit = () => {
    setEditing(false);
    if (value.trim() && value.trim() !== output.title) onRename(value.trim());
    else setValue(output.title);
  };
  const onKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") commit();
    if (e.key === "Escape") {
      setValue(output.title);
      setEditing(false);
    }
  };

  return (
    <div className="flex items-center gap-2 min-w-0">
      {editing ? (
        <Input autoFocus value={value} onChange={(e) => setValue(e.target.value)} onBlur={commit} onKeyDown={onKeyDown} aria-label="Output title" className="h-9 text-lg font-semibold" />
      ) : (
        <h2 className="truncate text-2xl font-semibold text-brand-navy">{output.title}</h2>
      )}
      {limit && (
        <span
          className={cn("shrink-0 rounded px-1.5 py-0.5 text-xs tabular-nums", used > limit.value ? "bg-red-100 text-red-800" : "bg-brand-teal/10 text-brand-teal")}
          title={`Title limit: ${limit.value} ${limit.unit}`}
        >
          {used} / {limit.value}
        </span>
      )}
      {canWrite && !editing && (
        <button type="button" disabled={saving} onClick={() => setEditing(true)} className="rounded p-1 text-muted-foreground hover:bg-muted" aria-label="Rename the output">
          <Pencil className="h-4 w-4" />
        </button>
      )}
    </div>
  );
};

interface SectionCardProps {
  section: Section;
  nested?: boolean;
  selected: boolean;
  editing: boolean;
  canWrite: boolean;
  saving: boolean;
  onSelect: () => void;
  onEdit: () => void;
  onSave: (json: NonNullable<Section["content_json"]>, text: string) => void;
  onCancel: () => void;
  onDirtyChange: (dirty: boolean) => void;
}

const SectionCard = ({ section, nested, selected, editing, canWrite, saving, onSelect, onEdit, onSave, onCancel, onDirtyChange }: SectionCardProps) => {
  const hasText = section.content_text.trim().length > 0 || !!section.content_json?.content?.length;
  const generation = useSectionGeneration(section.id);

  return (
    <article
      onClick={onSelect}
      className={cn("rounded-md border bg-card px-6 py-5 shadow-sm transition-shadow", nested && "ml-6", selected && "ring-2 ring-brand-teal/40")}
      aria-label={section.title}
    >
      <header className="mb-3 flex items-center justify-between gap-2">
        <h3 className={cn("font-semibold text-brand-navy", nested ? "text-base" : "text-lg")}>{section.title}</h3>
        {generation.pending && (
          <span className="flex items-center gap-1.5 text-sm text-brand-teal">
            <Loader2 className="h-3.5 w-3.5 animate-spin" /> Generating…
          </span>
        )}
        {canWrite && !editing && !generation.pending && (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="h-7 gap-1 text-brand-teal"
            onClick={(e) => {
              e.stopPropagation();
              onEdit();
            }}
          >
            <Pencil className="h-3.5 w-3.5" /> Edit
          </Button>
        )}
      </header>
      {generation.pending && generation.streamingText ? (
        <div className="prose prose-sm max-w-none whitespace-pre-wrap">{generation.streamingText}</div>
      ) : editing ? (
        <SectionEditor json={section.content_json} text={section.content_text} wordLimit={section.word_limit} saving={saving} onSave={onSave} onCancel={onCancel} onDirtyChange={onDirtyChange} />
      ) : hasText ? (
        <>
          <SectionContent json={section.content_json} text={section.content_text} />
          {section.citations && section.citations.length > 0 && <SectionCitations citations={section.citations} sources={section.sources} />}
        </>
      ) : generation.streamingText ? (
        <div className="prose prose-sm max-w-none whitespace-pre-wrap text-muted-foreground">{generation.streamingText}</div>
      ) : (
        <p className="text-sm italic text-muted-foreground">No text yet. Generate it from the section's sources, or click Edit to write it.</p>
      )}
    </article>
  );
};

interface DraftCanvasProps {
  output: Output;
  sections: Section[];
  selectedId: string | null;
  editingId: string | null;
  canWrite: boolean;
  savingTitle: boolean;
  savingSection: boolean;
  onSelect: (id: string) => void;
  onEdit: (id: string) => void;
  onSave: (section: Section, json: NonNullable<Section["content_json"]>, text: string) => void;
  onCancel: () => void;
  onDirtyChange: (dirty: boolean) => void;
  onRenameOutput: (title: string) => void;
}

/** The middle of the First draft screen: the output's title and every section's text, editable one section at a time. */
const DraftCanvas = (props: DraftCanvasProps) => {
  const { output, sections, selectedId, editingId, canWrite, savingSection, onSelect, onEdit, onSave, onCancel, onDirtyChange } = props;
  const card = (section: Section, nested = false) => (
    <SectionCard
      key={section.id}
      section={section}
      nested={nested}
      selected={section.id === selectedId}
      editing={section.id === editingId}
      canWrite={canWrite}
      saving={savingSection}
      onSelect={() => onSelect(section.id)}
      onEdit={() => onEdit(section.id)}
      onSave={(json, text) => onSave(section, json, text)}
      onCancel={onCancel}
      onDirtyChange={onDirtyChange}
    />
  );

  return (
    <section className="rounded-lg border bg-card p-5 min-w-0" aria-label="Draft">
      <div className="mb-4 flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <OutputTitle output={output} canWrite={canWrite} saving={props.savingTitle} onRename={props.onRenameOutput} />
          <p className="mt-0.5 text-sm text-muted-foreground">{draftState(sections)}</p>
        </div>
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <span>
                <Button type="button" variant="outline" size="sm" className="gap-1.5" disabled>
                  <FileDown className="h-4 w-4" /> Export Output and Bibliography
                </Button>
              </span>
            </TooltipTrigger>
            <TooltipContent>Export is available once Senior QC is complete.</TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </div>

      <div className="space-y-4 rounded-md bg-muted/60 p-4">
        {sections.length === 0 && <p className="py-8 text-center text-sm text-muted-foreground">Add a section in the outline to start writing.</p>}
        {sections
          .filter((s) => s.parent_id === null)
          .map((top) => (
            <div key={top.id} className="space-y-3">
              {card(top)}
              {childrenOf(sections, top.id).map((child) => card(child, true))}
            </div>
          ))}
      </div>
    </section>
  );
};

export default DraftCanvas;
